<template>
  <DialogRoot :open="isOpen" @update:open="onDialogOpenChange">
    <DialogPortal>
      <Transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="isOpen" class="dialog-viewer fixed inset-0 z-60 overflow-y-auto">
          <DialogOverlay class="fixed inset-0 backdrop-blur-xs backdrop-brightness-50" />
          <div
            class="relative z-60 flex min-h-full items-center justify-center p-4 text-center select-none"
          >
            <Transition
              enter-active-class="duration-300 ease-out"
              enter-from-class="scale-95"
              enter-to-class="scale-100"
              leave-active-class="duration-200 ease-in"
              leave-from-class="scale-100"
              leave-to-class="scale-95"
            >
              <DialogContent
                v-if="isOpen"
                class="surface max-w-[600px] transform overflow-hidden rounded-md p-6 text-left align-middle shadow-xl transition-all md:min-w-[25%]"
              >
                <DialogTitle as="h3" class="text-lg font-medium">
                  {{ currentDialog?.title }}
                </DialogTitle>
                <DialogDescription class="mt-2 text-sm whitespace-pre-line">
                  {{ currentDialog?.body }}
                </DialogDescription>

                <div>
                  <component
                    v-if="currentDialog?.template"
                    :is="currentDialog.template"
                    v-model="currentDialog.templateProps.modelValue"
                  />
                </div>

                <div class="mt-4 flex space-x-2">
                  <GeneralButton
                    v-if="confirmButtonText && currentDialog?.type !== DialogTypes.NO_BUTTON"
                    :class="currentDialog?.classConfirm ? currentDialog?.classConfirm : ''"
                    :disabled="waitTimeLeftSec > 0"
                    @click="onUserConfirm"
                    class="min-w-14"
                  >
                    <span>{{ confirmButtonText }}</span>
                    <span v-if="waitTimeLeftSec > 0" class="ml-1">({{ waitTimeLeftSec }})</span>
                  </GeneralButton>
                  <GeneralButton
                    v-if="currentDialog?.type === DialogTypes.CONFIRM_DECLINE"
                    color="error"
                    @click="onUserDecline"
                  >
                    {{ declineButtonText }}
                  </GeneralButton>
                  <GeneralButton
                    v-if="
                      currentDialog &&
                      [DialogTypes.CONFIRM, DialogTypes.CONFIRM_DECLINE].includes(
                        currentDialog.type
                      )
                    "
                    variant="outlined"
                    @click="onUserCancel"
                  >
                    {{ cancelButtonText }}
                  </GeneralButton>
                  <GeneralButton
                    v-if="currentDialog?.type === DialogTypes.CUSTOM"
                    @click="onUserCustom"
                  >
                    {{ customButtonText }}
                  </GeneralButton>
                </div>
              </DialogContent>
            </Transition>
          </div>
        </div>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue"
import {
  DialogRoot,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogOverlay,
  DialogPortal
} from "reka-ui"
import { storeToRefs } from "pinia"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import GeneralButton from "@/components/inputs/GeneralButton.vue"

const dialogStore = useDialogStore()
const { currentDialog } = storeToRefs(dialogStore)

let countdownTimer: ReturnType<typeof setTimeout> | null

const defaultWaitTimeSec = 1

const waitTimeLeftSec = ref(0)

const isOpen = computed(() => {
  return !!currentDialog.value
})

const defaultConfirmButtonTextMap: any = {
  [DialogTypes.OK]: "Oké",
  [DialogTypes.CONFIRM]: "Bevestigen",
  [DialogTypes.NO_BUTTON]: null,
  [DialogTypes.CUSTOM]: null
}

const confirmButtonText = computed(() => {
  if (currentDialog.value) {
    if (currentDialog.value.textConfirm) {
      return currentDialog.value.textConfirm
    }
    return defaultConfirmButtonTextMap[currentDialog.value.type]
  }
  return null
})

const declineButtonText = computed(() => {
  if (currentDialog.value?.textDecline) {
    return currentDialog.value.textDecline
  }
  return "Afwijzen"
})

const cancelButtonText = computed(() => {
  if (currentDialog.value?.textCancel) {
    return currentDialog.value.textCancel
  }
  return "Annuleren"
})

const customButtonText = computed(() => {
  if (currentDialog.value?.textCustom) {
    return currentDialog.value.textCustom
  }
  return "Annuleren"
})

function onUserConfirm() {
  if (currentDialog.value?.onConfirm) {
    currentDialog.value.onConfirm()
  }
  currentDialog.value = null
}

function onUserDecline() {
  if (currentDialog.value?.onDecline) {
    currentDialog.value.onDecline()
  }
  currentDialog.value = null
}

function onUserCancel() {
  if (currentDialog.value?.onCancel) {
    currentDialog.value.onCancel()
  }
  currentDialog.value = null
}

function onUserCustom() {
  if (currentDialog.value?.onCustom) {
    currentDialog.value.onCustom()
  }
}

function onUserEscape() {
  if (currentDialog.value?.lock) {
    return
  }
  onUserCancel()
}

function onDialogOpenChange(open: boolean) {
  if (!open) {
    onUserEscape()
  }
}

let storedActiveElement: HTMLElement | null = null
watch(currentDialog, (newVal, oldVal) => {
  if (newVal) {
    storedActiveElement = document.activeElement as HTMLElement // Temporarily store the active element to refocus it later.
  } else if (oldVal?.restoreOriginalFocus === undefined || oldVal?.restoreOriginalFocus) {
    // Default behavior is to restore focus to the element that was focused before the dialog was opened.
    if (storedActiveElement) {
      ;(storedActiveElement as HTMLElement).focus()
    }
  }

  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
  if (newVal) {
    waitTimeLeftSec.value = defaultWaitTimeSec
    if (newVal.mandatedWaitTimeSec !== undefined) {
      waitTimeLeftSec.value = newVal.mandatedWaitTimeSec
    }
    countdownTimer = setInterval(() => {
      waitTimeLeftSec.value--
      if (countdownTimer && waitTimeLeftSec.value <= 0) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }, 1000)
  }
})

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
})
</script>

<style scoped></style>
